import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  PodFooter,
  ProductFuelType,
  PodSection,
  PodSpacer,
  PodRow,
  PodTapper,
  ProductCompare,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  ProductCustomOptions,
  BundleDataModel
} from '@thd-olt-component-react/product-pod';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { ProductSeeSimilarItems, ViewSimilarIcon } from '@thd-olt-component-react/product-pod-group';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { Promotion } from '@thd-olt-component-react/promotion';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';
import { useProductPodClickTracker } from '../../hooks/useProductPodClickTracker';

export const handleSuperSkuMiniSwatchChange = ({
  superSkuChangedItemIdsRef,
  clickData,
  onChange
}) => {
  if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
    superSkuChangedItemIdsRef.current = [
      ...superSkuChangedItemIdsRef.current,
      clickData.itemId
    ];
  }

  onChange(clickData);
};

export const createSuperSkuMiniSwatchOnChangeHandler = ({
  superSkuChangedItemIdsRef,
  onChange
}) => (clickData) => handleSuperSkuMiniSwatchChange({
  superSkuChangedItemIdsRef,
  clickData,
  onChange
});

export const CustomProductPianoPodMobile = ({
  itemId,
  parentId,
  storeId,
  position,
  hasInStoreFilter,
  parent,
  impProductPodData,
  showProjectQuantity,
  hasSameDayFilter,
  url,
  isFulfillmentFilterAndMsgEnabled,
  isViewSimilarButtonEnabled,
  minimumOrderQuantity
}) => {
  const superSkuChangedItemIdsRef = useRef([]);
  const showFulfillmentPodStore = isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  const showFulfillmentPodShipping = isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;
  const minOrderQty = minimumOrderQuantity > 0 ? minimumOrderQuantity : undefined;

  const productPodNodeRef = useProductPodClickTracker({ itemId, position });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      className="product-pod"
      data-testid="product-pod"
    >
      <ProductPod
        analyticsData={{ parent, position }}
        analyticImpressionData={impProductPodData}
        hasSSKU
        itemId={itemId}
        storeId={storeId}
        padding="sui-p-2"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
              href={url}
              aria-label="Link"
            >
              <span className="sui-sr-only">View product details</span>
            </a>
            <div className="sui-relative sui-grid sui-h-full">
              {/* Parent of Col 1 and Col 2 */}
              <PodSection
                display="sui-grid"
                gridColumns="sui-grid-cols-11"
                gap="sui-gap-2"
                noPadding
              >
                {/* Col 1 - ProductImage + Super Sku Mini Swatches */}
                {/* PodMediaContainer */}
                <div
                  className="sui-bg-subtle sui-grid sui-col-span-5
                    sui-h-full sui-rounded-lg"
                  style={{ gridTemplateRows: '1fr auto 1fr' }}
                >
                  {/* PodSticker - Badges */}
                  <div className="sui-z-20 sui-flex sui-items-start sui-justify-start">
                    <PodSpacer padding={['LEFT']}>
                      <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    </PodSpacer>
                  </div>
                  {/* / PodSticker - Badges */}
                  {/* ProductImage */}
                  <div className="sui-z-20 sui-flex sui-items-center sui-justify-center">
                    <ProductImage
                      itemId={pod.itemId}
                      showSecondaryImage={false}
                      isBlendedImageWithBackground
                    />
                  </div>
                  {/* / ProductImage */}
                  {/* Super Sku Mini Swatches */}
                  <div className="sui-grid sui-w-full">
                    <div className="sui-grid sui-w-full sui-p-1 sui-z-20 sui-content-end">
                      <ProductCustomOptions itemId={pod.itemId} />
                      <ProductSample itemId={pod.itemId} />
                      <SuperSkuMiniSwatches
                        storeId={storeId}
                        itemId={pod.itemId}
                        onChange={createSuperSkuMiniSwatchOnChangeHandler({
                          superSkuChangedItemIdsRef,
                          onChange: pod.onChange
                        })}
                        onHover={pod.onSSKUHover}
                        componentImpressionData={{
                          name: 'ProductPod',
                          component: 'ProductPod',
                          position: position + 1,
                          type: 'product',
                          anchorSku: parentId
                        }}
                        isImpressionsRequired
                        hideComponentWhenEmpty
                        removeStaticHeight
                        swatchMediaSize="6"
                      />
                      <ProductServiceAddOns itemId={pod.itemId} />
                    </div>
                  </div>
                  {/* / Super Sku Mini Swatches */}
                </div>
                {/* / Col 1 - Pod Media Container */}
                {/* Col 2 - ProductHeader + ProductRating + ProductIdentifier + rest of pod */}
                <PodSection
                  display="sui-grid sui-col-span-6"
                  gap="sui-gap-0"
                  order="sui-order-2 md:sui-order-3"
                  alignTop
                  noPadding
                  columnAlign
                >
                  <PodTapper fitWidth zIndex>
                    <PodSpacer>
                      <ProductSponsored itemId={pod.itemId} position={position} noBackground noPadding />
                      <ProductHeader
                        brand="inline"
                        brandLineHeight="tight"
                        brandFontSize="sm"
                        productNameLineHeight="tight"
                        itemId={pod.itemId}
                        brandMaxLine={1}
                        titleMaxLine={3}
                        disableShopThisCollection
                      />
                    </PodSpacer>
                  </PodTapper>
                  <PodSection noPadding columnAlign padding="sui-py-1">
                    <PodTapper fitWidth zIndex>
                      <ProductIdentifier itemId={pod.itemId} type="model" />
                    </PodTapper>
                  </PodSection>
                  <PodSection noPadding alignTop columnAlign>
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      spanPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <PodSpacer
                        padding={['TOP']}
                        disable={pod.productSubType === 'HDQC'}
                      >
                        <Price
                          clsRemediation={{ placeholders: true, preservePlaceholders: true }}
                          disableOuterSpacing
                          itemId={pod.itemId}
                          large={false}
                          storeId={storeId}
                          displayEachUom={false}
                          onSavingsCenterToggle={pod.onSavingsCenterToggle}
                          hideForProductTypes={['HDQC']}
                          stackWasPrice
                        />
                      </PodSpacer>
                    </PodTapper>
                  </PodSection>
                  <PodSection noPadding alignTop columnAlign>
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      spanPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <PodSpacer padding={['TOP']}>
                        <Promotion
                          itemId={pod.itemId}
                          hideConditionalPromoDescription
                          pageType={parent.toUpperCase()}
                        />
                      </PodSpacer>
                    </PodTapper>
                  </PodSection>
                  <PodSection noPadding alignTop columnAlign padding="sui-py-1">
                    <PodTapper fitWidth zIndex>
                      <ProductRating itemId={pod.itemId} />
                    </PodTapper>
                  </PodSection>
                  {/* Fulfillment + Pod Actions */}
                  <PodFooter noPadding margin="sui-mt-auto">
                    <PodSection display="sui-grid" columnAlign noPadding alignEnd>
                      <PodTapper
                        zIndex
                        aPointer
                        buttonPointer
                        spanPointer
                        cursorPointer
                        handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                      >
                        <PodSpacer minHeight="60px">
                          {
                            showFulfillmentPodStore
                            && (
                              <PodSpacer padding={['TOP', 'BOTTOM']} hideForProductTypes={['HDQC']}>
                                <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} isNarrowPod />
                              </PodSpacer>
                            )
                          }
                          {
                            showFulfillmentPodShipping
                            && (
                              <PodSpacer
                                padding={['B2']}
                                disable={pod.productSubType === 'HDQC' || pod.productSubType === 'SFI'}
                              >
                                <FulfillmentPodShipping
                                  itemId={pod.itemId}
                                  storeId={pod.storeId}
                                  hasSameDayFilter={hasSameDayFilter}
                                  showFasterPodSpacing
                                  showFSDBMMessage
                                  isNarrowPod
                                  isAppliancePod
                                />
                              </PodSpacer>
                            )
                          }
                        </PodSpacer>
                      </PodTapper>
                    </PodSection>
                    <PodSection display="sui-grid" gap="sui-gap-0" columnAlign noPadding alignEnd>
                      <PodTapper zIndex>
                        <PodRow noPadding>
                          <div className="sui-flex-1">
                            <ProductATC
                              variant="primary"
                              hasInStoreFilter={hasInStoreFilter}
                              showProjectQuantity={showProjectQuantity}
                              storeId={storeId}
                              itemId={pod.itemId}
                              checkGeneric
                              outline
                              quantity={minOrderQty}
                            />
                            <ProductSeeSimilarItems
                              itemId={pod.itemId}
                              position={position}
                              productPodRef={pod.ref}
                              storeId={storeId}
                            />
                            <PodSpacer disable={pod.productSubType !== 'HDQC'}>
                              <NonBuyableQuoteCenter
                                itemId={pod.itemId}
                                checkForProductSubType={['HDQC']}
                              />
                            </PodSpacer>
                          </div>
                          {isViewSimilarButtonEnabled?.isViewSimilarBottomButtonEnabled && (
                            <div className="sui-flex sui-items-center sui-pl-2">
                              <ViewSimilarIcon
                                itemId={pod.itemId}
                                products={isViewSimilarButtonEnabled?.productArray}
                                storeId={storeId}
                              />
                            </div>
                          )}
                        </PodRow>
                      </PodTapper>
                    </PodSection>
                  </PodFooter>
                </PodSection>
                {/* / Col 2 - ProductHeader + ProductRating + ProductIdentifier + rest of pod */}
              </PodSection>
              {/* / Parent of Col 1 and Col 2 */}
            </div>
          </>
        )}
      />
    </div>
  );
};

CustomProductPianoPodMobile.displayName = 'CustomProductPianoPodMobile';

CustomProductPianoPodMobile.propTypes = {
  itemId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  hasInStoreFilter: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  impProductPodData: PropTypes.shape({}),
  parent: PropTypes.string,
  showProjectQuantity: PropTypes.bool,
  hasSameDayFilter: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool,
  isViewSimilarButtonEnabled: PropTypes.shape({
    isViewSimilarTopButtonEnabled: PropTypes.bool,
    isViewSimilarBottomButtonEnabled: PropTypes.bool,
    productArray: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  minimumOrderQuantity: PropTypes.number
};

CustomProductPianoPodMobile.defaultProps = {
  impProductPodData: null,
  parent: 'plp',
  showProjectQuantity: false,
  hasSameDayFilter: false,
  isFulfillmentFilterAndMsgEnabled: false,
  parentId: null,
  isViewSimilarButtonEnabled: {
    isViewSimilarTopButtonEnabled: false,
    isViewSimilarBottomButtonEnabled: false,
    productArray: []
  },
  minimumOrderQuantity: 0,
};

CustomProductPianoPodMobile.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  Promotion,
  Price,
  PriceClearanceDataModel,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductCompare,
  ProductFuelType,
  ProductSample,
  ProductHeader,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  ProductSeeSimilarItems,
  NonBuyableQuoteCenter,
  BundleDataModel,
  ViewSimilarIcon
);